/**
 * @file        src/layout/Sidebar.jsx
 * @description Right sidebar — wordmark + nav items (Hotels / Flights /
 *              Insurance) + wallet + Log in/Profile button. Mobile drawer
 *              toggles via store (navOpen). Desktop collapse via navCollapsed.
 *              Ported pixel-exact from the design HTML's <aside id="sidebar">.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-16 Claude — Corporate block's empty-name fallback "Company" →
 *                          "Your company". `agencyName` is empty on every live
 *                          session (no auth endpoint returns `agency_name` yet),
 *                          so this fallback is what the card ALWAYS shows — and
 *                          "Company" reads as a real company name, so the card
 *                          looked like it was working while naming nobody.
 *   - 2026-09-10 Claude — The logo block (uploaded-logo-or-wordmark, ~13 inline
 *                          <path> elements) moved out to components/BrandLogo.jsx
 *                          so the mobile home hero can render the same mark without
 *                          a second copy to keep in step. Rendering here is
 *                          unchanged — BrandLogo takes the width this file already
 *                          derived from navCollapsed. `useCompanyInfo` went with it.
 *   - 2026-08-18 Claude — Icon refresh: the shared glyphs (close, chevrons, check,
 *                          map pin, arrows, search, calendar) redrawn as one
 *                          consistent filled 24×24 family. Path data only — every
 *                          <svg> wrapper, class and size is unchanged.
 *   - 2026-08-18 Claude — Nav gained "Exclusive Offer" (client request), sitting
 *                          between Insurance and Contact us so the order reads
 *                          Hotels / Flights / Holidays / Insurance / Exclusive
 *                          Offer / Contact us / FAQ's — products first, then the
 *                          deals across them, then support. It opens /offers: the
 *                          same offer cards the home page shows, all of them, on a
 *                          page of their own.
 *                          Rendered as a real <a href> rather than a bare onClick
 *                          like its neighbours: a plain click still navigates
 *                          in-app (so Back returns to the screen and scroll
 *                          position the customer left), while ctrl/cmd-click and
 *                          middle-click open it in a genuine new browser tab, which
 *                          an onClick-only <a> silently swallows.
 *   - 2026-08-18 Claude — Nav links darkened from `text-muted` to `text-ink` (client
 *                          request): the secondary-text token is #64748B in light, so
 *                          the primary navigation read as washed-out grey. `ink` is
 *                          #0F172A light / white dark, keeping the dark theme legible —
 *                          a hardcoded black would have disappeared there. Icons share
 *                          the inherited colour (`fill="currentColor"`) and darken with
 *                          the labels; hover/collapsed states are untouched.
 *   - 2026-08-17 Claude — Nav gained "Contact us" and "FAQ's" entries, sitting at the
 *                          foot of the nav list (i.e. directly ABOVE the wallet row in
 *                          the footer block, client request). Both pages already
 *                          existed and were only linked from the SiteFooter, which is
 *                          not mounted on every route. This re-adds the Contact link
 *                          removed on 2026-08-07 — same page, now paired with FAQ's
 *                          and labelled to match the footer's Company column.
 *   - 2026-08-17 Claude — "Packages" nav label renamed to "Holidays" (client request).
 *                          The /packages ROUTE is deliberately unchanged: it is linked
 *                          from the footer, the home page and the /tours/* redirects,
 *                          and any live/shared URL would 404 if it moved.
 *   - 2026-08-07 Claude — Removed the "Contact" nav entry (client request), and
 *                          its now-unused goContact handler. The /contact ROUTE
 *                          and page are untouched; the SiteFooter still links
 *                          there, so the page stays reachable from any page that
 *                          mounts the footer.
 *   - 2026-08-04 Claude — Footer order: the account button moves ABOVE the theme
 *                          pill, so it reads wallet → account → theme in both the
 *                          expanded and collapsed footers. Its divider is now
 *                          unconditional — the account button is always above it,
 *                          signed in or not, so there is always something to divide.
 *   - 2026-08-04 Claude — Wallet row is SIGNED-IN ONLY: a wallet belongs to an
 *                          account, and logged out the link only re-opened the auth
 *                          modal that the button below already opens.
 *   - 2026-08-04 Claude — Footer order is now wallet → theme pill → auth button.
 *                          The theme pill moved ABOVE the auth button (still right-
 *                          aligned) and the button went back to full width on its
 *                          own row, which is what lets "Log in / Create account" sit
 *                          on ONE line: 176px of text area against the label's
 *                          ~152px. Sharing a row with the 48px pill left ~126px, so
 *                          it either clipped or had to wrap — both rejected. The
 *                          divider above the pill is conditional: logged out there
 *                          is no wallet row above it, so it would rule under nothing.
 *   - 2026-08-04 Claude — Removed the footer row's logged-out "Sign in" button. That
 *                          slot is the WALLET BALANCE readout, so only its signed-out
 *                          state is gone (the balance still renders once loggedIn) —
 *                          it duplicated the "Log in / Create account" button 12px
 *                          below it, both firing the same nh:open-auth event.
 *   - 2026-08-04 Claude — Auth button relabelled "Log in / Create account" and sized
 *                          to hold it: text-sm + px-3. The sidebar is 16rem with
 *                          px-6, so the old px-4 left a 176px text area — ~30px short
 *                          of the label at 16px bold, and `truncate` was clipping it.
 *   - 2026-08-04 Claude — The footer's three auth entries collapsed into ONE:
 *                          "Log in / Create account". "Sign in / Register" and
 *                          "Corporate login" dispatched the SAME nh:open-auth event
 *                          and opened the SAME modal, so the pair advertised a
 *                          choice nothing acted on; "New company? Register here" is
 *                          now a link under the modal's Corporate tab. Their two
 *                          handlers went with them. Ported from the Beyond build so
 *                          both storefronts sign in the same way.
 *   - 2026-08-03 Claude — Dropped the "Light mode" / "Dark mode" caption next to the
 *                          theme pill (client request). The toggle now renders with
 *                          showLabel={false} and without `row`; the wrapper carries
 *                          justify-end so the pill keeps its position.
 *   - 2026-07-20 Claude — Tightened the dead space around the theme row: the
 *                          divider's mt-1/pt-3 → -mt-0.5/pt-2, and the aside's
 *                          py-8 → pt-8 pb-4 (the 32px bottom inset was sized for
 *                          a two-line footer that no longer exists).
 *   - 2026-07-20 Claude — Footer Terms & Privacy links removed from the sidebar;
 *                          the Light/Dark pill is now centred alone on the row.
 *                          The documents stay reachable at /legal/* and remain
 *                          linked from the auth modal and checkout consent lines.
 *   - 2026-07-20 Claude — Privacy link repointed from /legal/hotel-privacy to the
 *                          unified /legal/privacy.
 *   - 2026-07-19 Claude — Footer legal links: "Hotel Terms · Flight Terms"
 *                          collapsed into a single "Terms & Conditions" link
 *                          (/legal/terms).
 *   - 2026-07-16 Claude — Footer row redesign: toggle left, legal links right-aligned
 *                          with · separators, over a hairline divider.
 *   - 2026-07-16 Claude — Theme fix: replaced hardcoded colors with theme tokens so the screen recolors on Light/Dark switch.
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-06-22 Claude — Move sidebar to left side: right-0 → left-0,
 *                          border-l → border-r, translate-x-full → -translate-x-full
 *                          (mobile closed), drawer-handle moves to -right-3 and
 *                          its corner becomes rounded-r-2xl / border-l-0,
 *                          mobile close button moves from left-4 → right-4.
 *   - 2026-06-22 Claude — Render the logo from navCollapsed state directly
 *                          instead of rendering both marks and relying on the
 *                          .nav-full/.nav-mini CSS toggle (left the wordmark
 *                          cramped when collapsed).
 *   - 2026-06-22 Claude — Collapsed sidebar shows the Neon Holidays wordmark
 *                          shrunk to w-16 (not the circular mark, which read as
 *                          a paper-plane icon); same SVG, just a smaller width.
 *   - 2026-06-22 Claude — Hotels/Flights nav entries open Home's search tabs.
 *   - 2026-07-06 Claude — Flights nav resets the flight flow first
 *                          (resetFlightSearch: one-way defaults, previous
 *                          search/selections/add-ons wiped) before opening the
 *                          Flights tab.
 *   - 2026-08-06 Claude — Added a "Contact" nav entry. /contact previously had
 *                          no link anywhere in the header/sidebar — only the
 *                          SiteFooter (not mounted on Packages/PackageDetail,
 *                          where a visitor is most likely to want it) linked
 *                          to it, so the page and the "Enquire now" flow that
 *                          lands there were effectively unreachable.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';
import ThemeToggle from '../components/ThemeToggle.jsx';
import BrandLogo from '../components/BrandLogo.jsx';

export default function Sidebar() {
  const navigate = useNavigate();
  const navOpen = useBooking((s) => s.navOpen);
  const closeNav = useBooking((s) => s.closeNav);
  const toggleNavCollapsed = useBooking((s) => s.toggleNavCollapsed);
  const navCollapsed = useBooking((s) => s.navCollapsed);
  const loggedIn = useBooking((s) => s.loggedIn);
  const walletBalance = useBooking((s) => s.walletBalance);
  // Tenant-wide wallet kill-switch (admin -> Wallet Management -> Wallet
  // Settings). Off = every wallet surface is hidden, nav included.
  const walletFeatureEnabled = useBooking((s) => s.walletFeatureEnabled) !== false;
  const userEmail = useBooking((s) => s.userEmail);
  const role = useBooking((s) => s.role);
  const agencyName = useBooking((s) => s.agencyName);
  const agentCode = useBooking((s) => s.agentCode);
  const isAgent = role === 'b2b_owner' || role === 'b2b_user';

  const goSearch = (kind) => {
    closeNav();
    if (kind === 'hotels') navigate('/', { state: { searchTab: 'hotels' } });
    else if (kind === 'flights') {
      // Fresh flight start: back to one-way, previous search (route/dates/pax/
      // airlines/fare type), selections and add-ons all wiped — the user gets
      // the default one-way form, not their last search.
      useBooking.getState().resetFlightSearch();
      navigate('/', { state: { searchTab: 'flights' } });
    }
    else if (kind === 'insurance') navigate('/insurance');
    else if (kind === 'packages') navigate('/packages');
  };

  // Plain content pages (Contact us / FAQ's). Same close-then-navigate shape as
  // goSearch, minus the search-tab / flow-reset handling those products need.
  const goPage = (to) => {
    closeNav();
    navigate(to);
  };


  const authBtnClick = () => {
    closeNav();
    if (loggedIn) navigate('/profile');
    else window.dispatchEvent(new CustomEvent('nh:open-auth'));
  };

  // Corporate accounts sign in with an emailed OTP — the same passwordless flow as
  // every other account, which is why their two dedicated buttons are gone from the
  // footer: `authBtnClick` opens the one modal, and the Personal / Corporate switch
  // (plus the "Register here" link) lives inside it.

  return (
    <aside
      id="sidebar"
      className={
        'fixed top-0 left-0 z-50 h-full bg-panel/95 backdrop-blur-xl border-r border-line flex flex-col px-6 pt-8 pb-4 lg:translate-x-0 transition-transform duration-300 ' +
        (navOpen ? 'translate-x-0' : '-translate-x-full')
      }
    >
      {/* collapse toggle (desktop) — drawer handle on the right (inner) edge */}
      <button
        onClick={toggleNavCollapsed}
        className="hidden lg:flex group absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-16 rounded-r-2xl bg-panel border border-l-0 border-line items-center justify-center cursor-pointer z-10 hover:bg-neon transition-colors"
        aria-label="Toggle menu"
      >
        <svg viewBox="0 0 24 24" className={'w-4 h-4 text-muted group-hover:text-onaccent transition-all duration-300 ' + (navCollapsed ? 'rotate-180' : '')}>
          <path fill="currentColor" d="m9 6 6 6-6 6z" />
        </svg>
      </button>

      {/* close (mobile) */}
      <button
        onClick={closeNav}
        className="lg:hidden absolute top-4 right-4 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center cursor-pointer hover:border-neon transition-colors"
        aria-label="Close menu"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted">
          <path fill="currentColor" d="M18.3 7.1 16.9 5.7 12 10.6 7.1 5.7 5.7 7.1l4.9 4.9-4.9 4.9 1.4 1.4 4.9-4.9 4.9 4.9 1.4-1.4-4.9-4.9z" />
        </svg>
      </button>

      <a
        onClick={() => { closeNav(); navigate('/', { state: { searchTab: 'flights' } }); }}
        className="flex flex-col items-center leading-none mb-10 cursor-pointer"
        aria-label="Neon Holidays home"
      >
        {/* The wordmark, or the logo uploaded in admin Site Settings — see
            components/BrandLogo.jsx. Full width expanded, shrunk when collapsed. */}
        <BrandLogo className={navCollapsed ? 'w-16' : 'w-[150px]'} />
      </a>

      {/* text-ink, not text-muted: these are the site's primary navigation, and the
          secondary-text token (#64748B in light) read as a washed-out grey against
          the panel. `ink` is #0F172A in light and white in dark, so the labels are
          black on the light theme without going invisible on the dark one. Each
          item's own `hover:text-onaccent` still wins on hover. */}
      <nav className="flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col gap-0.5 text-[15px] font-medium text-ink">
        <a onClick={() => goSearch('hotels')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M3 21V7a2 2 0 0 1 2-2h6v4h8a2 2 0 0 1 2 2v10zM7 9H5v2h2zm4 0H9v2h2zm6 4h-2v2h2z" /></svg>
          <span className="nav-label">Hotels</span>
        </a>
        <a onClick={() => goSearch('flights')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" /></svg>
          <span className="nav-label">Flights</span>
        </a>
        <a onClick={() => goSearch('packages')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M9 2h6a2 2 0 0 1 2 2v2h3.5A1.5 1.5 0 0 1 22 7.5V11H2V7.5A1.5 1.5 0 0 1 3.5 6H7V4a2 2 0 0 1 2-2m0 4h6V4H9zM2 12.8h8.6v2H13.4v-2H22V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm9.9 0h.2v.2h-.2z" /></svg>
          <span className="nav-label">Holidays</span>
        </a>
        <a onClick={() => goSearch('insurance')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5z" /></svg>
          <span className="nav-label">Insurance</span>
        </a>

        {/* Exclusive Offer — the deals that cut across every product, so it sits
            after them and before the support pair. `href` + preventDefault, not a
            bare onClick: the href is what makes ctrl/cmd-click and middle-click
            open a real new tab, and what lets the browser show the destination on
            hover; the handler keeps a plain click as an in-app navigation, so the
            page can be closed with Back onto the screen it was opened from. */}
        <a
          href="/offers"
          onClick={(e) => {
            // Let the browser handle the modified clicks it already understands
            // (new tab / new window / download) instead of hijacking them.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            goPage('/offers');
          }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors"
        >
          {/* price tag — the one icon in this list that reads as "a deal" rather
              than as another product. */}
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M21.4 11.6 12.4 2.6A2 2 0 0 0 11 2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 .6 1.4l9 9a2 2 0 0 0 2.8 0l7-7a2 2 0 0 0 0-2.8M6.5 8A1.5 1.5 0 1 1 8 6.5 1.5 1.5 0 0 1 6.5 8" /></svg>
          <span className="nav-label">Exclusive Offer</span>
        </a>

        {/* Support pages, last in the list so they sit directly above the footer's
            wallet row. They live in the same <nav> rather than in the footer block
            because the wallet row is signed-in only — put here they render for
            every visitor, and they collapse to icon-only with the rest. */}
        <a onClick={() => goPage('/contact')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M12 2.2A9.3 9.3 0 0 0 2.7 11.5v5.2A3.1 3.1 0 0 0 5.8 19.8h1.4a1 1 0 0 0 1-1v-5.4a1 1 0 0 0-1-1H4.8a7.2 7.2 0 0 1 14.4 0h-2.4a1 1 0 0 0-1 1v5.4a1 1 0 0 0 1 1h1.4a3.1 3.1 0 0 0 3.1-3.1v-5.2A9.3 9.3 0 0 0 12 2.2" /></svg>
          <span className="nav-label">Contact us</span>
        </a>
        <a onClick={() => goPage('/faqs')} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neon hover:text-onaccent cursor-pointer transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0"><path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20m0 16.4a1.35 1.35 0 1 1 0-2.7 1.35 1.35 0 0 1 0 2.7m2-6.5c-.75.53-.95.85-.95 1.45v.45h-2.1v-.55c0-1.35.52-2.08 1.55-2.8.83-.6 1.15-.93 1.15-1.55 0-.72-.6-1.24-1.55-1.24-.98 0-1.66.52-1.77 1.45H8.25c.1-2.07 1.65-3.42 3.85-3.42s3.6 1.25 3.6 3.1c0 1.15-.52 1.87-1.7 2.71" /></svg>
          <span className="nav-label">FAQ's</span>
        </a>
      </nav>

      <div className="mt-auto pt-6">
        {/* Full footer */}
        <div className="nav-foot flex flex-col gap-3">
          {/* Wallet — SIGNED-IN ONLY. A wallet belongs to an account, so with no
              session there is no balance to link to and the row just bounced the
              visitor into the auth modal, which the button below already does.
              Both halves (the link and the balance) live or die together.
              Also hidden outright when the tenant has switched the wallet off in
              the admin console (Wallet Management -> Wallet Settings): the whole
              feature is unavailable, so a balance in the nav would link to a page
              that can neither spend nor top up. */}
          {loggedIn && walletFeatureEnabled ? (
            <div className="flex items-center justify-between text-sm font-medium px-1">
              <button
                onClick={() => { closeNav(); navigate('/wallet'); }}
                className="inline-flex items-center gap-1.5 text-muted hover:text-ink cursor-pointer"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon"><path fill="currentColor" d="M20.5 7H5.2a1 1 0 0 1 0-2H19V3H5.2A3.2 3.2 0 0 0 2 6.2v11.6A3.2 3.2 0 0 0 5.2 21h15.3a1.5 1.5 0 0 0 1.5-1.5V8.5A1.5 1.5 0 0 0 20.5 7M17 15.1a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2" /></svg>
                Wallet
              </button>
              <button
                onClick={() => { closeNav(); navigate('/wallet'); }}
                className="font-bold cursor-pointer text-neon hover:brightness-110"
              >
                {inr(walletBalance)}
              </button>
            </div>
          ) : null}
          {/* Corporate identity: a signed-in corporate user books on behalf of
              their company, so surface which account they're acting under. */}
          {loggedIn && isAgent ? (
            <div className="px-3 py-2 rounded-xl border border-neon/40 bg-neon/10">
              <p className="text-[10px] font-bold uppercase tracking-wide text-neon">Corporate mode</p>
              <p className="text-xs font-semibold text-ink truncate">{agencyName || 'Your company'}</p>
              {agentCode ? <p className="text-[10px] text-muted">{agentCode}</p> : null}
            </div>
          ) : null}

          {/* ONE auth button, full width on its OWN row. This used to be three
              stacked entries — "Sign in / Register", "Corporate login" and "New
              company? Register here" — where the first two dispatched the SAME
              event and opened the SAME modal, so two of the three only implied a
              choice the app did not make. Personal vs corporate is now a switch
              inside AuthModal, and the company self-registration link lives under
              its corporate tab.

              WIDTH BUDGET — this is why the button gets the row to ITSELF rather
              than sharing it with the theme pill. The sidebar is 16rem (256px) with
              px-6 and this row keeps px-1 to align with the wallet row, so the
              button spans 200px = 176px of text inside px-3. "Log in / Create
              account" measures ~152px at 14px DM Sans bold, so it sits on ONE line
              with ~24px spare. Beside the 48px pill it had ~126px and clipped.
              text-sm + px-3 is also why it fits at all — the original 16px + px-4
              left 176px and was ~30px short at that size. `truncate` stays as the
              backstop for a signed-in user's email, which shares this button and is
              a single unbreakable token. */}
          <button
            onClick={authBtnClick}
            title={loggedIn ? (userEmail || 'My profile') : 'Log in / Create account'}
            className="w-full block truncate px-3 py-3 rounded-xl bg-neon text-onaccent font-bold text-sm hover:brightness-110 cursor-pointer transition-colors text-center"
          >
            {loggedIn ? (userEmail || 'My profile') : 'Log in / Create account'}
          </button>

          {/* Theme pill — right-aligned and LAST, under the account button: the
              footer reads wallet → account → theme. The divider is unconditional
              because the account button is always above it, signed in or not. */}
          <div className="-mt-0.5 pt-2 px-1 flex justify-end border-t border-line/60">
            <ThemeToggle variant="sky" size="sm" showLabel={false} />
          </div>
        </div>
        {/* Mini footer (collapsed) — same order as the expanded one: account button
            first, theme toggle underneath. */}
        <div className="nav-foot-mini flex-col gap-3">
          <button
            onClick={authBtnClick}
            className="w-11 h-11 mx-auto rounded-xl bg-neon text-onaccent flex items-center justify-center cursor-pointer hover:brightness-110 transition-colors"
            title="Log in / Create account"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10m0 2c-5 0-9 2.5-9 5.5V22h18v-2.5c0-3-4-5.5-9-5.5" /></svg>
          </button>
          <ThemeToggle mini />
        </div>
      </div>
    </aside>
  );
}
